import React, { useState, useEffect } from 'react';
import api from '../api/client';

const GRADE_LABELS = {1:'1年',2:'2年',3:'3年',4:'4年',5:'5年',6:'6年'};
const SCORE_COLORS = {1:'#ef4444',2:'#f97316',3:'#eab308',4:'#22c55e',5:'#3b82f6'};
const SCORE_LABELS = {1:'要支援',2:'努力中',3:'標準',4:'優秀',5:'卓越'};

export default function IndividualEvalTab({ schoolType }) {
  const grades = schoolType === 'junior' ? [1,2,3] : [1,2,3,4,5,6];
  const [selectedGrade, setSelectedGrade] = useState('');
  const [selectedSubjectName, setSelectedSubjectName] = useState('');
  const [selectedSubjectId, setSelectedSubjectId] = useState('');
  const [subjectNames, setSubjectNames] = useState([]);
  const [units, setUnits] = useState([]);
  const [evaluating, setEvaluating] = useState(false);
  const [results, setResults] = useState(null);
  const [currentSubject, setCurrentSubject] = useState(null);
  const [expandedRow, setExpandedRow] = useState(null);
  const [progress, setProgress] = useState(0);

  useEffect(() => {
    if (!selectedGrade) { setSubjectNames([]); setSelectedSubjectName(''); setUnits([]); setSelectedSubjectId(''); return; }
    api.get(`/subjects/by-grade-subject?grade=${selectedGrade}`)
      .then(r => {
        const names = [...new Set(r.data.map(s => s.subject_name))];
        setSubjectNames(names);
        setSelectedSubjectName('');
        setUnits([]);
        setSelectedSubjectId('');
      }).catch(() => {});
  }, [selectedGrade]);

  useEffect(() => {
    if (!selectedSubjectName) { setUnits([]); setSelectedSubjectId(''); return; }
    api.get(`/subjects/by-grade-subject?grade=${selectedGrade}&subject_name=${encodeURIComponent(selectedSubjectName)}`)
      .then(r => { setUnits(r.data); setSelectedSubjectId(''); })
      .catch(() => {});
  }, [selectedSubjectName]);

  const handleEvaluate = async () => {
    if (!selectedSubjectId) return;
    setEvaluating(true);
    setResults(null);
    setProgress(0);
    const prog = setInterval(() => setProgress(p => Math.min(p + 3, 90)), 800);
    try {
      const r = await api.get(`/evaluate/individual?subject_id=${selectedSubjectId}`);
      setResults(r.data.results);
      setCurrentSubject(r.data.subject);
      setProgress(100);
    } catch(e) {
      alert('評価の取得に失敗しました');
    } finally {
      clearInterval(prog);
      setEvaluating(false);
    }
  };

  const handleCSV = () => {
    if (!results || !currentSubject) return;
    const rows = [
      ['学年','組','番号','振り返り回数','5段階評価','評価コメント','評価根拠'],
      ...results.map(r => [
        r.grade + '年', r.class_name, r.number + '番', r.review_count,
        r.score || '-',
        '"' + (r.comment||'').replace(/"/g,'""') + '"',
        '"' + (r.basis||'').replace(/"/g,'""') + '"'
      ])
    ];
    const csv = '\uFEFF' + rows.map(r => r.join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `個人評価_${currentSubject.subject_name}_${currentSubject.unit_name||''}.csv`;
    a.click();
  };

  const groupByClass = (results) => {
    const groups = {};
    for (const r of results) {
      if (!groups[r.class_name]) groups[r.class_name] = [];
      groups[r.class_name].push(r);
    }
    return groups;
  };

  return (
    <div style={{ padding: '20px' }}>
      <h2 style={{ fontSize: '18px', fontWeight: 'bold', marginBottom: '16px' }}>個人別AI評価</h2>
      <p style={{ color: '#6b7280', marginBottom: '20px', fontSize: '14px' }}>
        学年・教科・単元を選択して実行すると、児童一人ひとりの評価コメントと5段階評価を生成します。
      </p>

      <div style={{ display: 'flex', gap: '12px', marginBottom: '20px', flexWrap: 'wrap', alignItems: 'flex-end' }}>
        <div>
          <label style={{ display: 'block', fontSize: '13px', color: '#374151', marginBottom: '4px' }}>学年</label>
          <select value={selectedGrade} onChange={e => setSelectedGrade(e.target.value)}
            style={{ padding: '8px 12px', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '14px' }}>
            <option value="">選択</option>
            {grades.map(g => <option key={g} value={g}>{GRADE_LABELS[g]}</option>)}
          </select>
        </div>
        <div>
          <label style={{ display: 'block', fontSize: '13px', color: '#374151', marginBottom: '4px' }}>教科</label>
          <select value={selectedSubjectName} onChange={e => setSelectedSubjectName(e.target.value)}
            disabled={!selectedGrade}
            style={{ padding: '8px 12px', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '14px', minWidth: '120px' }}>
            <option value="">選択</option>
            {subjectNames.map(n => <option key={n} value={n}>{n}</option>)}
          </select>
        </div>
        <div>
          <label style={{ display: 'block', fontSize: '13px', color: '#374151', marginBottom: '4px' }}>単元</label>
          <select value={selectedSubjectId} onChange={e => setSelectedSubjectId(e.target.value)}
            disabled={!selectedSubjectName}
            style={{ padding: '8px 12px', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '14px', minWidth: '200px' }}>
            <option value="">選択</option>
            {units.map(u => <option key={u.id} value={u.id}>{u.unit_name || u.subject_name}</option>)}
          </select>
        </div>
        <button onClick={handleEvaluate} disabled={!selectedSubjectId || evaluating}
          style={{ padding: '8px 20px', background: evaluating ? '#9ca3af' : '#2563eb', color: 'white',
            border: 'none', borderRadius: '6px', fontSize: '14px', cursor: evaluating ? 'not-allowed' : 'pointer', fontWeight: 'bold' }}>
          {evaluating ? 'AI評価中...' : 'AI評価を実行'}
        </button>
        {results && (
          <button onClick={handleCSV}
            style={{ padding: '8px 16px', background: '#059669', color: 'white', border: 'none', borderRadius: '6px', fontSize: '14px', cursor: 'pointer' }}>
            CSVで保存
          </button>
        )}
      </div>

      {evaluating && (
        <div style={{ marginBottom: '20px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px', fontSize: '13px', color: '#6b7280' }}>
            <span>AIが児童ごとに評価を生成しています...</span>
            <span>{progress}%</span>
          </div>
          <div style={{ background: '#e5e7eb', borderRadius: '999px', height: '8px' }}>
            <div style={{ background: '#2563eb', height: '8px', borderRadius: '999px', width: `${progress}%`, transition: 'width 0.5s' }} />
          </div>
        </div>
      )}

      {results && currentSubject && (
        <div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
            <h3 style={{ fontWeight: 'bold', fontSize: '16px' }}>
              {currentSubject.subject_name}「{currentSubject.unit_name || currentSubject.subject_name}」評価結果
            </h3>
            <span style={{ fontSize: '13px', color: '#6b7280' }}>
              対象: {results.filter(r => r.review_count > 0).length}名 / 全{results.length}名
            </span>
          </div>

          <div style={{ display: 'flex', gap: '8px', marginBottom: '20px', flexWrap: 'wrap' }}>
            {[5,4,3,2,1].map(s => {
              const count = results.filter(r => r.score === s).length;
              return (
                <div key={s} style={{ background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: '8px', padding: '8px 14px', textAlign: 'center', minWidth: '70px' }}>
                  <div style={{ fontSize: '20px', fontWeight: 'bold', color: SCORE_COLORS[s] }}>{count}</div>
                  <div style={{ fontSize: '11px', color: '#6b7280' }}>{s}({SCORE_LABELS[s]})</div>
                </div>
              );
            })}
            <div style={{ background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: '8px', padding: '8px 14px', textAlign: 'center', minWidth: '70px' }}>
              <div style={{ fontSize: '20px', fontWeight: 'bold', color: '#9ca3af' }}>{results.filter(r => !r.score).length}</div>
              <div style={{ fontSize: '11px', color: '#6b7280' }}>未記録</div>
            </div>
          </div>

          {Object.entries(groupByClass(results)).map(([className, students]) => (
            <div key={className} style={{ marginBottom: '24px' }}>
              <h4 style={{ fontWeight: 'bold', marginBottom: '8px', color: '#1e40af', fontSize: '15px' }}>
                {currentSubject.grade}年{className}
              </h4>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '14px' }}>
                <thead>
                  <tr style={{ background: '#f1f5f9' }}>
                    <th style={thStyle}>番号</th>
                    <th style={thStyle}>振り返り回数</th>
                    <th style={thStyle}>5段階評価</th>
                    <th style={{ ...thStyle, width: '40%' }}>評価コメント</th>
                    <th style={thStyle}>根拠</th>
                  </tr>
                </thead>
                <tbody>
                  {students.map(r => (
                    <React.Fragment key={r.number}>
                      <tr style={{ borderBottom: '1px solid #e5e7eb', background: expandedRow === `${className}-${r.number}` ? '#f0f9ff' : 'white' }}>
                        <td style={tdStyle}>{r.class_name} {r.number}番</td>
                        <td style={{ ...tdStyle, textAlign: 'center' }}>
                          {r.review_count > 0
                            ? <span style={{ background: '#dbeafe', color: '#1d4ed8', padding: '2px 8px', borderRadius: '12px', fontSize: '13px' }}>{r.review_count}回</span>
                            : <span style={{ color: '#9ca3af' }}>未記録</span>}
                        </td>
                        <td style={{ ...tdStyle, textAlign: 'center' }}>
                          {r.score
                            ? <span style={{ background: SCORE_COLORS[r.score], color: 'white', padding: '3px 10px', borderRadius: '12px', fontSize: '13px', fontWeight: 'bold' }}>{r.score} {SCORE_LABELS[r.score]}</span>
                            : <span style={{ color: '#9ca3af' }}>-</span>}
                        </td>
                        <td style={{ ...tdStyle, color: '#374151', lineHeight: '1.5' }}>{r.comment || '-'}</td>
                        <td style={{ ...tdStyle, textAlign: 'center' }}>
                          {r.basis && (
                            <button onClick={() => setExpandedRow(expandedRow === `${className}-${r.number}` ? null : `${className}-${r.number}`)}
                              style={{ fontSize: '12px', color: '#2563eb', background: 'none', border: '1px solid #93c5fd', borderRadius: '4px', padding: '2px 8px', cursor: 'pointer' }}>
                              {expandedRow === `${className}-${r.number}` ? '閉じる' : '根拠'}
                            </button>
                          )}
                        </td>
                      </tr>
                      {expandedRow === `${className}-${r.number}` && r.basis && (
                        <tr style={{ background: '#f0f9ff' }}>
                          <td colSpan={5} style={{ padding: '10px 16px', fontSize: '13px', color: '#1e3a5f', borderBottom: '1px solid #bfdbfe' }}>
                            <strong>評価根拠：</strong>
                            <span style={{ whiteSpace: 'pre-line', marginLeft: '8px' }}>{r.basis}</span>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  ))}
                </tbody>
              </table>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

const thStyle = { padding: '10px 12px', textAlign: 'left', fontWeight: '600', fontSize: '13px', color: '#374151', borderBottom: '2px solid #e2e8f0' };
const tdStyle = { padding: '10px 12px', verticalAlign: 'top' };
